import React, { useMemo } from 'react';
import { AlertTriangle, Download } from 'lucide-react';
import {
    NOTFALL_FELDER, notfallVorschlaege, notfallWert, notrufLuecken,
} from '../bestandUebernahme';
import type { ProjektBestand } from '../useProjektBestand';
import { TextFeld, Untertitel } from './bausteine';

/**
 * Die Notfallangaben des Plans (`asplaene.notfall`).
 *
 * ═══ WARUM ES DIESE KARTE GIBT ════════════════════════════════════════════
 * Die Felder wurden GELESEN — vom Aushang „Verhalten im Notfall" und vom
 * Notfallausweis — und von keiner Oberflaeche GESCHRIEBEN. Der Aushang druckte
 * deshalb in jedem Plan „—". Ein Notfallblatt mit Gedankenstrichen statt
 * Nummern ist schlimmer als gar keines: es sieht aus wie eine geprueft-leere
 * Angabe, und davor steht im Ernstfall jemand, der eine Nummer braucht.
 *
 * ═══ VORSCHLAEGE, KEINE VORBELEGUNG ═══════════════════════════════════════
 * Was sich aus dem Projekt ableiten laesst, steht als Vorschlag mit seiner
 * QUELLE daneben und wird auf Klick eingetragen. Nie stillschweigend: eine
 * Vorbelegung, die sich selbst speichert, mutiert Bestandsdatensaetze
 * rueckwirkend (Handbuch, Planungsphase). Und: die Giftnotrufnummer wird
 * nicht erfunden — es ist die im Repo belegte Nummer, die dieselbe Anwendung
 * auf dem SiGe-Aushang und im SiGe-Handbuch druckt.
 *
 * Gespeichert wird beim VERLASSEN des Feldes (`TextFeld`), nicht bei jedem
 * Zeichen: jeder Schreibvorgang laedt das Buendel neu und kostete sonst den
 * Fokus.
 *
 * ═══ WARUM DER BESTAND ALS PROP KOMMT ═════════════════════════════════════
 * Diese Karte lud den Notrufplan frueher selbst. Zusammen mit der
 * Bestandskarte und dem Aushangreiter waren das drei unabhaengige Staende
 * derselben Quelle — bei Notfallnummern. Gelesen wird jetzt einmal im
 * Container; hier kommt das Ergebnis samt `bereit` an.
 *
 * ═══ WARUM `bereit` DIE LUECKENLISTE SPERRT ═══════════════════════════════
 * Ohne gelesenen Notrufplan meldet `notrufLuecken` „keine Notfallstelle
 * hinterlegt" — eine Behauptung ueber fehlende Notfalldaten, die niemand
 * geprueft hat. Sie stand bei JEDEM Oeffnen des Reiters kurz da, auch bei
 * vollstaendig gepflegtem Notrufplan, und verschwand dann wieder. Solange
 * nicht gelesen ist, sagt die Karte dazu nichts.
 */

const KASTEN = 'bg-white border border-slate-200 rounded-2xl p-4 mb-4';

interface Props {
    /** Stammdaten und Notrufplan — im Container EINMAL gelesen. */
    bestand: ProjektBestand;
    /** `asplaene.notfall` — freie jsonb-Spalte, Schluessel siehe `NOTFALL_FELDER`. */
    notfall: Record<string, unknown>;
    gesperrt: boolean;
    /** Bekommt NUR die geaenderten Schluessel; der Aufrufer mischt sie ein. */
    onSpeichern: (aenderung: Record<string, unknown>) => void;
}

export const NotfallKarte: React.FC<Props> = ({
    bestand, notfall, gesperrt, onSpeichern,
}) => {
    const { bereit, metadata, notrufplan } = bestand;

    // Vor der Antwort wird NICHTS ueber den Notrufplan behauptet.
    const luecken = useMemo(
        () => bereit ? notrufLuecken(notrufplan, notfall) : [],
        [bereit, notrufplan, notfall]);

    // Ein Vorschlag verschwindet, sobald sein Wert dasteht — er soll nicht
    // zum Dauerknopf werden, der bei jedem Blick nach Handlung aussieht.
    const offeneVorschlaege = useMemo(
        () => notfallVorschlaege(metadata).filter(v => notfallWert(notfall, v.key) !== v.wert),
        [metadata, notfall]);

    return (
        <section className={KASTEN}>
            <h2 className="font-bold text-slate-800 mb-0.5">Notfall- und Brandschutzangaben</h2>
            <p className="text-xs text-slate-500 mb-3">
                Diese Angaben stehen auf dem Aushang „Verhalten im Notfall“ und auf dem
                Notfallausweis. Was hier leer bleibt, wird dort farbig als „nicht hinterlegt“
                ausgewiesen — ausdrücklich kein Gedankenstrich, der wie eine geprüfte Angabe
                aussähe. Klinik und Durchgangsarzt kommen aus dem Notrufplan des SiGe-Plans und
                werden nicht hier gepflegt.
            </p>

            {luecken.length > 0 && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 mb-3">
                    <p className="font-bold mb-1 flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        Der Aushang „Verhalten im Notfall“ ist noch unvollständig
                    </p>
                    <ul className="space-y-0.5">
                        {luecken.map((l, i) => <li key={i}>· {l}</li>)}
                    </ul>
                </div>
            )}

            {!gesperrt && offeneVorschlaege.length > 0 && (
                <div className="mb-4">
                    <Untertitel
                        text="Aus dem Bestand vorgeschlagen"
                        hinweis="Wird erst auf Klick eingetragen. Die Quelle steht jeweils daneben — geraten wird nichts."
                    />
                    <ul className="space-y-1.5">
                        {offeneVorschlaege.map(v => (
                            <li
                                key={v.key}
                                className="flex items-start gap-2 text-sm border border-slate-200 rounded-lg px-2.5 py-2"
                            >
                                <span className="min-w-0 flex-1">
                                    <b className="text-slate-800">{v.label}:</b>{' '}
                                    <span className="font-semibold text-slate-900">{v.wert}</span>
                                    <span className="block text-[11px] text-slate-500">{v.quelle}</span>
                                </span>
                                <button
                                    type="button"
                                    onClick={() => onSpeichern({ [v.key]: v.wert })}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-stone-700 text-white
                                               rounded-lg text-[11px] font-bold shrink-0"
                                >
                                    <Download className="w-3 h-3" /> Übernehmen
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3">
                {NOTFALL_FELDER.map(f => (
                    <div key={f.key} className={f.mehrzeilig ? 'sm:col-span-2' : undefined}>
                        <TextFeld
                            label={f.label}
                            wert={notfallWert(notfall, f.key)}
                            gesperrt={gesperrt}
                            mehrzeilig={f.mehrzeilig}
                            hinweis={f.hinweis}
                            onSpeichern={v => onSpeichern({ [f.key]: v })}
                        />
                    </div>
                ))}
            </div>
        </section>
    );
};
